import { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '@/shared/stores/authStore';
import { Eye, EyeOff, TrendingUp, Users, Plus, ArrowUpRight, ArrowDownRight, Clock } from 'lucide-react';
import Header from '@/react-app/components/Header';
import { api } from '@/shared/api/endpoints';
import type { UserStats, Transaction } from '@/shared/types';
import { useNavigate } from "react-router-dom";
import { Link } from 'react-router';
import { toast } from "react-toastify";
import newDashboardPrev from "../../assets/new-dashboard-preview.png";
import { ReferralSEO } from "@/react-app/components/seo";
import { calculateIncrementalReturn } from "@/shared/utils/incrementalRet";

export default function Dashboard() {
  const { user: authStoreUser, isAuthenticated } = useAuthStore();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [balanceVisible, setBalanceVisible] = useState(true);

  const [copiedStates, setCopiedStates] = useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();

  useEffect(() => {
    if (authStoreUser) {
      fetchUserData();
    }
  }, [authStoreUser]);


  const fetchUserData = async () => {
    try {
      const [statsResponse, transactionsResponse] = await Promise.all([
        api.user.getStats(),
        api.user.getTransactions()
      ]);

      if (statsResponse.success) {
        setStats(statsResponse.data);
      } else {
        console.error('Failed to fetch user stats:', statsResponse.error);
      }

      if (transactionsResponse.success) {
        setTransactions(transactionsResponse.data?.transactions || []);
      } else {
        console.error('Failed to fetch user transactions:', transactionsResponse.error);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = new Date();

    const formatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: false,
      timeZone: userTimeZone,
    });

    const hour = parseInt(formatter.format(now), 10);

    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getUserDisplayName = () => {
    // Try to get name from authStore user first (from email/password registration)
    if (authStoreUser?.firstName) {
      return authStoreUser.firstName + " " + authStoreUser.lastName;
    }

    // Final fallback
    return 'Saver';
  };

  const formatCurrency = (amount: number) => {
    return balanceVisible ? `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '****';
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownRight className="h-4 w-4 text-green-500" />;
      case 'monthly_roi':
        return <TrendingUp className="h-4 w-4 text-blue-500" />;
      case 'referral_bonus':
        return <Users className="h-4 w-4 text-purple-500" />;
      case 'investment_closure':
        return <ArrowUpRight className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // const getPackageIcon = () => {
  //   return <TrendingUp className="h-5 w-5 text-blue-500" />;
  // };

  const redirect = () => {
    navigate("/financials")
  }

  const save = () => {
    navigate("/save")
  }



  const handleShare = async () => {
    const shareUrl = `https://tradeenrich.com/register?ref=${stats?.referral_code}`;
    const imageUrl = 'https://tradeenrich.com/promo_1.jpeg';
    const messageText = `Join TradeEnrich Today and Start Earning Like Me!\n\nUse my code: ${stats?.referral_code}\n\nRegister here: ${shareUrl}`;


    try {
      // 1. Prepare base data
      // const shareData: any = {
      //   title: 'Join TradeEnrich & Start Earning',
      //   text: `Join me on TradeEnrich using my code ${stats?.referral_code} and let's earn together!`,
      //   url: shareUrl,
      // };
      const shareData: any = {
        title: 'TradeEnrich Earnings',
        text: messageText,
        url: shareUrl
      };

      // 2. Try to attach an image (Works on most mobile browsers)
      if (navigator.canShare && navigator.share) {
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const file = new File([blob], "promo_1.jpeg", { type: 'image/jpeg' });

          // Check if the browser specifically allows sharing this file
          if (navigator.canShare({ files: [file] })) {
            shareData.files = [file];

            // delete shareData.url;
          }
        } catch (fileErr) {
          console.log("Image fetch failed, falling back to text-only share", fileErr);
          // shareData.url = shareUrl;
        }

        await navigator.share(shareData);
      } else {
        // Fallback for Desktop/Unsupported browsers (Facebook)
        const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        window.open(fbUrl, '_blank', 'width=600,height=400');
      }
    } catch (err) {
      console.log('Error sharing:', err);
    }
  };

  const { accruedSoFar, dailyPercentage } = useMemo(() => {
    if (!stats?.monthlyReturn) return { accruedSoFar: 0, dailyPercentage: "0.0" };

    return calculateIncrementalReturn(stats.monthlyReturn, stats.activePackages[0].nextWithdrawalDate);
  }, [stats?.monthlyReturn]);

  //Now you can safely return or show a loader
  if (!stats) return <p></p>;


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header variant="dashboard" />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  const copyToClipboard = async (text: string, field: string) => {
    if (!text || text === 'Not provided') return;

    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [field]: true }));

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [field]: false }));
      }, 2000);
      toast.success("Link copied");
    } catch (error) {
      console.error('Failed to copy text:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedStates(prev => ({ ...prev, [field]: true }));
        setTimeout(() => {
          setCopiedStates(prev => ({ ...prev, [field]: false }));
        }, 2000);
      } catch (fallbackError) {
        console.error('Fallback copy failed:', fallbackError);
      }
      document.body.removeChild(textArea);
    }
    console.log(copiedStates);
  };

  if (!isAuthenticated) {
    navigate("/login")
    return null;
  }






  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header variant="dashboard" />
      <ReferralSEO stats={stats} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Greeting */}
        {/* <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {getGreeting()}, {getUserDisplayName()}! 👋
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Here's what's happening with your savings today.
          </p>

        </div> */}

        <div className="my-4 md:my-0 px-4">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white flex flex-wrap items-center gap-1">
            <span className="whitespace-nowrap text-black">
              {getGreeting()},
            </span>

            <span className="text-tahiti_gold whitespace-nowrap">
              {getUserDisplayName()} 👋
            </span>
          </h1>

          <p className="text-gray-500 dark:text-gray-400 mt-1 sm:mt-2">
            Here's what's happening with your savings today.
          </p>

          <button
            onClick={handleShare}
            className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-purple-50 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
            Share Ref Link
          </button>
        </div>

        {/* Your Dashboard Preview */}
        <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 rounded-3xl p-8 mb-8 text-white relative overflow-hidden shadow-2xl">
          {/* Background decorations */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full transform translate-x-32 -translate-y-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-white/5 to-transparent rounded-full transform -translate-x-24 translate-y-24"></div>

          <div className="relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                  Your Dashboard Overview
                </h2>
                <p className="text-purple-100 text-lg mb-6">
                  Real-time overview of your savings progress and earnings
                </p>

                {/* Mini Dashboard Preview */}
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex items-center mb-2">
                        <TrendingUp className="h-4 w-4 text-blue-200 mr-2" />
                        <span className="text-xs text-purple-200">Wallet Balance</span>
                      </div>
                      <div className="text-xl font-bold">{formatCurrency(stats?.totalBalance || 0)}</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex items-center mb-2">
                        <ArrowDownRight className="h-4 w-4 text-green-300 mr-2" />
                        <span className="text-xs text-purple-200">Total Deposited</span>
                      </div>
                      <div className="text-xl font-bold">{formatCurrency(stats?.totalDeposits || 0)}</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex items-center mb-2">
                        <TrendingUp className="h-4 w-4 text-orange-300 mr-2" />
                        <span className="text-xs text-purple-200">Total Withdrawn</span>
                      </div>
                      <div className="text-xl font-bold">{formatCurrency(stats?.totalEarnings || 0)}</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex items-center mb-2">
                        <Users className="h-4 w-4 text-purple-300 mr-2" />
                        <span className="text-xs text-purple-200">Referral Earnings</span>
                      </div>
                      <div className="text-xl font-bold">{formatCurrency(stats?.totalReferralBonus || 0)}</div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-purple-200">All Packages:</span>
                      <span className="font-semibold capitalize">
                        {stats?.current_package === 'none' ? 'Not Selected' : stats?.activePackages.length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
                  <img
                    src={newDashboardPrev}
                    alt="Trade Enrich Dashboard Preview"
                    className="w-full h-auto rounded-xl shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Stats Section with Preview Design */}
        <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-3xl p-8 mb-8 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/20 dark:to-purple-900/20 rounded-full -z-10 transform translate-x-32 -translate-y-32 opacity-50"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-indigo-100 to-blue-100 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-full -z-10 transform -translate-x-24 translate-y-24 opacity-30"></div>

          <div className="relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Total Balance - Enhanced Design */}
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/50 dark:border-gray-700/50 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <TrendingUp className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total Monthly Return</h3>
                  </div>
                  <button
                    onClick={() => setBalanceVisible(!balanceVisible)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    {balanceVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {formatCurrency(stats?.monthlyReturn || 0)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full inline-block">Overall monthly earnings</p>

              </div>

              {/* Total Deposited - Enhanced Design */}
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/50 dark:border-gray-700/50 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Accrued Profit</h3>
                </div>

                <p className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                  {/* This will now show $338.70 if it's day 21 of $500 total */}
                  {formatCurrency(accruedSoFar)}
                </p>

                <div className="flex items-center">
                  <div className="flex items-center bg-green-100 dark:bg-green-900/50 px-2 py-1 rounded-full">
                    <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse mr-2" />
                    <span className="text-xs font-medium text-green-700 dark:text-green-400">
                      +{dailyPercentage}% added today
                    </span>
                  </div>
                </div>
              </div>

              {/* Total Earnings - Enhanced Design */}
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/50 dark:border-gray-700/50 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center shadow-lg">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total Withdrawn</h3>
                </div>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400 mb-2">
                  {formatCurrency(stats?.totalEarnings || 0)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full inline-block">From savings returns</p>
              </div>

              {/* Referral Bonus - Enhanced Design */}
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl p-6 shadow-xl border border-white/50 dark:border-gray-700/50 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Referral Bonus</h3>
                </div>
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
                  {formatCurrency(stats?.totalReferralBonus || 0)}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/50 px-2 py-1 rounded-full inline-block">
                  +{formatCurrency(stats?.monthly_referral_bonus || 0)} this month
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions - Enhanced Design */}
          <div className="lg:col-span-1">
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/50 dark:border-gray-700/50 mb-6 relative overflow-hidden">
              {/* Background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-3xl"></div>

              <div className="relative z-10">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <Plus className="h-4 w-4 text-white" />
                  </div>
                  Quick Actions
                </h3>
                <div className="space-y-4">
                  <Link
                    to="/save"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-2xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center space-x-2 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
                    <Plus className="h-5 w-5" />
                    <span>Save</span>
                  </Link>
                  <button
                    onClick={redirect}
                    className="w-full bg-white/70 dark:bg-gray-700/70 backdrop-blur-sm text-gray-700 dark:text-gray-300 p-4 rounded-2xl font-medium hover:bg-white dark:hover:bg-gray-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border border-gray-200/50 dark:border-gray-600/50">
                    View Payment History
                  </button>
                  <button
                    onClick={save}
                    className="w-full bg-white/70 dark:bg-gray-700/70 backdrop-blur-sm text-gray-700 dark:text-gray-300 p-4 rounded-2xl font-medium hover:bg-white dark:hover:bg-gray-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border border-gray-200/50 dark:border-gray-600/50">
                    Upgrade Package
                  </button>
                </div>
              </div>
            </div>

            {/* Current Package - Enhanced Design */}
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/50 dark:border-gray-700/50 relative overflow-hidden">
              {/* Background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-blue-50/50 dark:from-green-900/10 dark:to-blue-900/10 rounded-3xl"></div>

              <div className="relative z-10">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <Users className="h-4 w-4 text-white" />
                  </div>
                  Referral Count
                </h3>
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/50 dark:to-purple-900/50 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                    <Users className="h-10 w-10 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h4 className="font-bold text-xl text-gray-900 dark:text-white capitalize mb-2">
                    {stats?.referralCount}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 bg-gray-100/70 dark:bg-gray-700/70 px-3 py-2 rounded-full inline-block">
                    {'Total referred users'}
                  </p>
                  {stats?.current_package === 'none' && (
                    <button className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-2xl text-sm font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
                      Choose Package
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Recent Transactions - Enhanced Design */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/50 dark:border-gray-700/50 relative overflow-hidden">
              {/* Background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 to-blue-50/30 dark:from-gray-900/30 dark:to-blue-900/10 rounded-3xl"></div>

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-3">
                      <Clock className="h-4 w-4 text-white" />
                    </div>
                    Recent Transactions
                  </h3>
                  <Link
                    to="/transactions"
                    className="text-blue-600 dark:text-blue-400 text-sm font-semibold hover:text-blue-700 dark:hover:text-blue-300 transition-colors bg-blue-50 dark:bg-blue-900/30 px-4 py-2 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900/50">
                    View All
                  </Link>
                </div>

                {transactions.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                      <Clock className="h-10 w-10 text-gray-400" />
                    </div>
                    <h4 className="text-gray-900 dark:text-white font-bold text-xl mb-3">No transactions yet</h4>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-6 max-w-sm mx-auto">
                      Make your first deposit to start earning returns and see your transaction history
                    </p>
                    <Link
                      to="/save"
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-2xl text-sm font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
                      Make Deposit
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.slice(0, 3).map((transaction) => (
                      <div key={transaction._id} className="flex items-center justify-between p-4 bg-white/70 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-600 dark:to-gray-700 rounded-2xl flex items-center justify-center shadow-lg">
                            {getTransactionIcon(transaction.type)}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {transaction.description || transaction.type}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {formatDate(transaction.createdAt)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold text-lg ${transaction.type === 'deposit'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                            }`}>
                            {transaction.type === 'monthly_roi' || transaction.type === 'referral_bonus' || transaction.type === 'investment_closure' ? '-' : '+'}
                            ${transaction.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </p>
                          <p className={`text-xs font-medium capitalize px-2 py-1 rounded-full ${transaction.status === 'completed'
                            ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/50'
                            : transaction.status === 'pending'
                              ? 'text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/50'
                              : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/50'
                            }`}>
                            {transaction.status}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>


            {/* Active savement Packages - Enhanced Design */}
            <div className="mt-8">
              <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl p-6 shadow-2xl border border-white/50 dark:border-gray-700/50 relative overflow-hidden">
                {/* Background gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-blue-50/30 dark:from-emerald-900/10 dark:to-blue-900/10 rounded-3xl"></div>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-lg flex items-center justify-center mr-3">
                        <TrendingUp className="h-4 w-4 text-white" />
                      </div>
                      Active Packages
                    </h3>
                    <Link
                      to="/packages"
                      className="text-emerald-600 dark:text-emerald-400 text-sm font-semibold hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors bg-emerald-50 dark:bg-emerald-900/30 px-4 py-2 rounded-full hover:bg-emerald-100 dark:hover:bg-emerald-900/50">
                      View All Packages
                    </Link>
                  </div>

                  {!stats?.activePackages || stats.activePackages.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                        <TrendingUp className="h-10 w-10 text-gray-400" />
                      </div>
                      <h4 className="text-gray-900 dark:text-white font-bold text-xl mb-3">No active packages</h4>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-6 max-w-sm mx-auto">
                        Start your savings journey by choosing a package to begin earning monthly returns
                      </p>
                      <Link
                        to="/save"
                        className="bg-gradient-to-r from-emerald-600 to-blue-600 text-white px-8 py-3 rounded-2xl text-sm font-semibold hover:from-emerald-700 hover:to-blue-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
                        Choose Package
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {stats?.activePackages.slice(0, 3).map((pack) => (
                        <div key={pack._id} className="flex items-center justify-between p-4 bg-white/70 dark:bg-gray-700/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50 rounded-2xl hover:bg-white dark:hover:bg-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                          <div className="flex items-center space-x-4">
                            {/* <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-600 dark:to-gray-700 rounded-2xl flex items-center justify-center shadow-lg">
                            {getTransactionIcon(pack.amount.toString())}
                          </div> */}
                            <div>
                              <p className="font-semibold text-gray-900 dark:text-white">
                                {pack?.packageId?.name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Start: {formatDate(pack?.startDate)}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Withdrawal: {formatDate(pack?.nextWithdrawalDate)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold text-lg ${pack?.status
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                              }`}>
                              ${pack?.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                            <p className={`text-xs font-medium capitalize px-2 py-1 rounded-full ${pack.status
                              ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/50'
                              : 'text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/50'
                              }`}>
                              Monthly ROI: ${pack?.monthlyROI}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>



        </div>



        {/* Referral Section - Enhanced Design */}
        {
          stats?.referral_code && (
            <div className="mt-8 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-white/10 to-transparent rounded-full -z-10 transform translate-x-32 -translate-y-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-white/5 to-transparent rounded-full -z-10 transform -translate-x-24 translate-y-24"></div>

              <div className="relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-xl rounded-2xl flex items-center justify-center mr-4">
                        <Users className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-3xl font-bold">Earn with Referrals</h3>
                    </div>
                    <p className="text-purple-100 mb-6 text-lg">
                      Share your unique referral link and earn bonus returns when friends join and start saving with Trade Enrich.
                    </p>
                    <div className="bg-white/20 backdrop-blur-xl rounded-2xl p-4 inline-block border border-white/30 shadow-xl">
                      <div className="text-xs text-purple-100 mb-1 uppercase tracking-wide">Your Referral Link</div>
                      <span className="font-mono text-2xl font-bold text-white text-sm sm:text-base text-white break-all">https://tradeenrich.com/register?ref={stats.referral_code}</span>
                    </div>
                  </div>
                  <div className="text-center lg:text-right">
                    <div className="inline-flex flex-col items-center">
                      <div className="w-24 h-24 bg-white/20 backdrop-blur-xl rounded-3xl flex items-center justify-center mb-6 shadow-2xl">
                        <Users className="h-12 w-12 text-white" />
                      </div>
                      <button
                        onClick={() => copyToClipboard(`https://tradeenrich.com/register?ref=${stats.referral_code}`, 'refLink')}
                        className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-purple-50 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
                        Copy Code

                      </button>

                    </div>
                  </div>
                  <div className="text-center lg:text-right">
                    <div className="inline-flex flex-col items-center">
                      {/* <div className="w-24 h-24 bg-white/20 backdrop-blur-xl rounded-3xl flex items-center justify-center mb-6 shadow-2xl">
                        <Users className="h-12 w-12 text-white" />
                      </div> */}
                      <button
                        onClick={handleShare}
                        className="bg-white text-purple-600 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-purple-50 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
                        Share Link
                      </button>
                      <p className="text-purple-200 text-sm mt-3 max-w-xs">
                        share directly on all socials
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      </main >
    </div >
  );
}
